export interface DesignHandleResolution {
  className: string
  authorityClassName: string
  handles: string[]
  unresolvedHandles: string[]
}

const DESIGN_HANDLE = /^@ds:([A-Za-z0-9._:-]+)$/

/**
 * Resolve authored class expressions before resolving the design handles those
 * expressions may select. A stateful class branch is allowed to return an
 * immutable `@ds:` role, but the inverse order leaves that returned role in
 * the DOM as an unresolved literal.
 */
export function resolveClassNameDesignAuthority(
  value: string,
  resolveSymbol: (expr: string) => unknown,
  classNameVariants?: Record<string, string>,
  variantKey?: string,
): DesignHandleResolution {
  let className = value
  if (className.includes("#{")) {
    const resolved = resolveSymbol(className)
    if (typeof resolved === "string") className = resolved
  }

  const resolvedVariantKey = variantKey?.includes("#{")
    ? resolveSymbol(variantKey)
    : variantKey
  const variantClassName = (
    typeof resolvedVariantKey === "string" && !resolvedVariantKey.includes("#{")
      ? classNameVariants?.[resolvedVariantKey] ?? classNameVariants?.default
      : undefined
  )
  if (variantClassName) className = `${className} ${variantClassName}`

  return resolveDesignHandles(className, resolveSymbol)
}

export function resolveDesignHandles(
  value: string,
  resolveSymbol: (expr: string) => unknown,
): DesignHandleResolution {
  const handles: string[] = []
  const authorityClasses: string[] = []
  const unresolvedHandles: string[] = []
  const className = value.replace(/\S+/g, (raw) => {
    if (!raw.startsWith("@ds:")) return raw
    const match = DESIGN_HANDLE.exec(raw)
    if (!match) {
      unresolvedHandles.push(raw)
      return raw
    }

    const handle = match[1]
    handles.push(handle)
    const resolved = resolveSymbol(`#{ds.${handle}}`)
    if (typeof resolved !== "string" || resolved.trim() === "") {
      unresolvedHandles.push(raw)
      return ""
    }
    authorityClasses.push(resolved.trim())
    return resolved
  })

  return {
    className: className.replace(/\s+/g, " ").trim(),
    authorityClassName: authorityClasses.join(" ").replace(/\s+/g, " ").trim(),
    handles,
    unresolvedHandles,
  }
}
